import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { request } from "hooks/useOkoApi/useOkoApi";
import { trackClickedButton } from "utils/analytics";
import { colors, logEducatorDashboard, LogLevel } from "utils";
import { isCurrentSchoolYear } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { WarmUpModal } from "components/pages/WarmUpPicker/modalParts";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const Title = styled.h2`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 32px;
  margin: 0;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 2px solid ${colors.nightlyWoods950};
  border-radius: 100px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  padding: 0;
  width: 48px;
`;

// Figma 5511:79469: the body sits 32px above the buttons; its own pieces are
// 16px apart (5511:79470).
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LessonBox = styled.div`
  background: ${colors.blue50};
  color: ${colors.nightlyWoods500};
  display: flex;
  flex-direction: column;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 18px;
  gap: 4px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 16px;
`;

const LessonName = styled.span`
  font-weight: 700;
`;

const GroupCount = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
`;

const Blurb = styled.p`
  color: ${colors.nightlyWoods500};
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 18px;
  letter-spacing: 0.25px;
  line-height: 26px;
  margin: 0;
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 32px;
`;

// Not in Figma: what the list says when there is no other class to copy to.
const NoOtherClasses = styled.p`
  color: ${colors.nightlyWoods500};
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  padding: 0 32px;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const CheckboxLabel = styled.label`
  align-items: center;
  color: ${(p) => (p.$disabled ? colors.gray700 : colors.nightlyWoods500)};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  display: flex;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  gap: 8px;
  height: 24px;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
`;

// A flex row, so no text line box (strut, baseline) is laid around the 24px
// checkbox: rows keep Figma's 24px height and 40px pitch (5511:79476).
const ClassRow = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
`;

const Box = styled.span`
  align-items: center;
  background: ${(p) => (p.$checked ? colors.green500 : "white")};
  border: 2px solid ${(p) => (p.$checked ? colors.green500 : p.$disabled ? "#ACAFAD" : colors.nightlyWoods500)};
  border-radius: 4px;
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;

  ${HiddenCheckbox}:focus-visible + label & {
    outline: 2px solid ${colors.blue500};
    outline-offset: 1px;
  }
`;

const Footer = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: flex-end;
`;

const FooterButton = styled.button`
  border-radius: 8px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// Figma's widths, 103 and 92, are the floor; "Saving…" may run wider.
const CancelButton = styled(FooterButton)`
  min-width: 103px;
  padding: 0 20px;
  background: #fff;
  border: 1px solid ${colors.blue500};
  color: ${colors.blue500};
`;

const SaveButton = styled(FooterButton)`
  min-width: 92px;
  padding: 0 16px;
  background: ${colors.blue500};
  border: none;
  color: ${colors.milkGlass500};
`;

const SaveError = styled.p`
  color: crimson;
  flex: 1;
  font-size: 14px;
  margin: 0;
`;

// "Copy Your Lesson To More Classes" (Figma 5059-63777). Eligibility comes from
// the account payload's per-class assignment_summary: a class must have at least
// `requiredCount` free codes (one per real-track group in the source bundle); a
// missing summary means ineligible. Save is one all-or-nothing POST — on success
// the parent gets the response's `copies` for banners, on a 400 (a target lost
// codes since the modal rendered) the account is refetched so the list re-greys.
const LESSON_TITLE = "Copy Your Lesson To More Classes";

const LESSON_BLURB = "Select one or more Classes to copy this lesson, including its related groups and question sets.";

// Why a class is greyed out, per Figma 5511-79462 / 5511-79487.
const INELIGIBLE_TIP = "This class can’t be selected because it doesn’t have enough "
  + "available group codes. Archive old lessons to clear additional group codes.";

// Figma 5511:79487: a 342px bubble plus its 16x12 tail, 358 in all.
const TIP_MAX_WIDTH = 342;

// 400 = a target no longer has enough free codes (assigned after the modal
// rendered); the message names the class. Refetch the account so the list
// reflects the new availability.
const lessonFailureOf = (e) => ({
  message: e?.detail || e?.message || "Failed to copy lesson.",
  refreshAccount: e?.status === 400,
});

const NOT_READ = { pending: false, byClass: null };

// The free codes per class from `load`, read on each opening and again on
// `reread`; none to read leaves the account's counts in charge.
const useFreeCodes = (isOpen, load) => {
  const [state, setState] = useState(NOT_READ);
  const [reads, setReads] = useState(0);
  useEffect(() => {
    if (!isOpen || !load) return undefined;
    let current = true;
    setState({ pending: true, byClass: null });
    load()
      .then((byClass) => current && setState({ pending: false, byClass }))
      .catch(() => current && setState(NOT_READ));
    return () => {
      current = false;
    };
  }, [isOpen, load, reads]);
  return { ...state, reread: () => setReads((n) => n + 1) };
};

const groupCountLine = (count) => (count === 1 ? "1 Group" : `${count} Groups`);

export const CopyLessonModal = ({
  assignmentType,
  isOpen,
  onClose,
  classroomId,
  assignmentId,
  lessonLabel,
  lessonName,
  classes = [],
  requiredCount,
  onCopied,
  onAccountRefresh,
  // The warm-up picker reuses this frame with its own texts.
  title = LESSON_TITLE,
  blurb = LESSON_BLURB,
  ineligibleTip = INELIGIBLE_TIP,
  // Resolves to each class's free codes by id, read once per opening. Until it
  // answers every row is disabled; if it fails, the account's
  // available_count decides.
  loadFreeCodes,
  // Sends the copy for the checked class ids, in list order, and resolves to
  // the response's copies; the lesson copy (with its "Copy Lesson" event) when
  // not given.
  save,
  // What a failed save shows and does: { message, refreshAccount,
  // rereadFreeCodes, gone }; the lesson's handling when not given.
  failureOf,
  // Called instead of showing a message when the failure says the source is gone.
  onGone,
}) => {
  const [selected, setSelected] = useState(() => new Set());
  const freeCodes = useFreeCodes(isOpen, loadFreeCodes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      setError(null);
    }
  }, [isOpen]);

  // Past-school-year classes are not copy targets — the class picker already keeps
  // them behind its "SY 25-26 Classes" group, and copying into one would land the
  // bundle where nobody is teaching. Same predicate the picker groups on. The
  // class being copied from is hidden too (Figma 5511:79518).
  const targetClasses = (classes || []).filter((c) => isCurrentSchoolYear(c) && c.id !== classroomId);

  const freeCodesOf = (c) => (freeCodes.byClass ? freeCodes.byClass[c.id] : c.assignment_summary?.available_count);
  const isEligible = (c) => !freeCodes.pending && (freeCodesOf(c) ?? -1) >= requiredCount;

  // A re-read of the free codes unchecks the classes it greys out.
  useEffect(() => {
    if (!freeCodes.byClass) return;
    setSelected((prev) => new Set([...prev].filter((id) => (freeCodes.byClass[id] ?? -1) >= requiredCount)));
  }, [freeCodes.byClass, requiredCount]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const copyLesson = (targets) =>
    request(`curriculum/classrooms/${classroomId}/assignments/${assignmentId}/copy`, {
      method: "POST",
      payload: { target_classroom_ids: targets },
    }).then((data) => {
      trackClickedButton("Copy Lesson", {
        assignment_type: assignmentType,
        page_name: "Dashboard",
        source: "copy_lesson_modal",
        class: classroomId || null,
        target_count: targets.length,
      });
      return data?.copies || [];
    });

  const handleSave = async () => {
    // Re-check eligibility at save time: the account may have refreshed (and
    // re-greyed classes) after a 400 while selections were already made.
    const targets = targetClasses.filter((c) => selected.has(c.id) && isEligible(c)).map((c) => c.id);
    if (targets.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const copies = await (save || copyLesson)(targets);
      onCopied?.(copies);
    } catch (e) {
      logEducatorDashboard("CopyLessonModal: failed to copy lesson", {
        level: LogLevel.WARNING,
        event: "copy_lesson_error",
        statusCode: e?.status,
        classroomId,
        assignmentId,
      });
      const failure = (failureOf || lessonFailureOf)(e);
      if (failure.gone) {
        onGone?.();
        return;
      }
      if (failure.refreshAccount) onAccountRefresh?.();
      if (failure.rereadFreeCodes) freeCodes.reread();
      setError(failure.message);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    // The warm-up modals' shell: no page scrollbar and no scrollbar padding
    // while open, so the dialog centres on the window (Figma x 383 at 1366).
    <WarmUpModal
      show={isOpen}
      onHide={onClose}
      centered
      dialogClassName="clm-modal-dialog"
      contentClassName="clm-modal-content clm-copy-modal-content"
      backdropClassName="clm-modal-backdrop"
      aria-label={title}
    >
      <Container>
        <Header>
          <Title>{title}</Title>
          <CloseButton aria-label="Close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" stroke={colors.nightlyWoods950} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </CloseButton>
        </Header>
        <Content>
          <Body>
            <LessonBox>
              {lessonLabel && <span>{lessonLabel}</span>}
              <LessonName>{lessonName}</LessonName>
              <GroupCount>{groupCountLine(requiredCount)}</GroupCount>
            </LessonBox>
            <Blurb>{blurb}</Blurb>
            {targetClasses.length === 0 ? (
              <NoOtherClasses>You have no other classes to copy to.</NoOtherClasses>
            ) : (
              <ClassList>
                {targetClasses.map((c) => {
                  const disabled = !isEligible(c);
                  const checked = selected.has(c.id);
                  const inputId = `copy-class-${c.id}`;
                  return (
                    <ClassRow key={c.id}>
                      <HiddenCheckbox
                        id={inputId}
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(c.id)}
                      />
                      {/* A disabled row explains itself: greying alone leaves the teacher
                          guessing why the class can't be picked. */}
                      <Tooltip
                        id={`${inputId}-tooltip`}
                        tip={disabled && !freeCodes.pending ? ineligibleTip : null}
                        placement="right"
                        maxWidth={TIP_MAX_WIDTH}
                        arrowSize="16x12"
                      >
                        <CheckboxLabel
                        htmlFor={inputId}
                        $disabled={disabled}
                        // A disabled box cannot take focus, so the row does:
                        // keyboard users reach the tooltip too.
                        tabIndex={disabled ? 0 : undefined}
                        aria-disabled={disabled ? "true" : undefined}
                        aria-describedby={disabled ? `${inputId}-tooltip` : undefined}
                      >
                          <Box $checked={checked} $disabled={disabled} aria-hidden>
                            {checked && <FontAwesomeIcon icon={faCheck} style={{ color: colors.milkGlass500, fontSize: "14px" }} />}
                          </Box>
                          <span>{c.name}</span>
                        </CheckboxLabel>
                      </Tooltip>
                    </ClassRow>
                  );
                })}
              </ClassList>
            )}
          </Body>
          <Footer>
            {error && <SaveError role="alert">{error}</SaveError>}
            <CancelButton type="button" onClick={onClose} disabled={saving}>
              Cancel
            </CancelButton>
            <SaveButton type="button" onClick={handleSave} disabled={saving || selected.size === 0}>
              {saving ? "Saving…" : "Save"}
            </SaveButton>
          </Footer>
        </Content>
      </Container>
    </WarmUpModal>
  );
};

// Figma 5511:79972: 990x56, its 1px stroke drawn inside. The CSS border adds
// 2px, which the close button's -1px margins give back.
const BannerFrame = styled.div`
  align-items: center;
  background: ${colors.blue50};
  border: 1px solid ${colors.blue500};
  border-radius: 8px;
  box-shadow: 0 1px 1.5px rgba(14, 26, 49, 0.10), 0 1px 1px rgba(14, 26, 49, 0.06);
  display: flex;
  gap: 16px;
  padding: 16px;
`;

const BannerText = styled.div`
  align-items: baseline;
  color: ${colors.nightlyWoods500};
  display: flex;
  flex: 1 0 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  gap: 16px;
  letter-spacing: 0.25px;
  line-height: 22px;
`;

const BannerLink = styled.button`
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 22px;
  padding: 0;
  text-decoration: underline;
`;

const BannerClose = styled.button`
  background: transparent;
  border: none;
  color: ${colors.nightlyWoods950};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  margin: -1px -1px -1px 0;
  padding: 0;
`;

// Toasts float over the page rather than pushing the content down. Above the
// sticky Create Groups bar (z-index 200). Figma 5511:79971 puts the stack 32px
// into the main content, 19px from the top, 990 wide. With no `left`, a fixed
// box keeps its in-flow left edge, GroupCodeView's 24px padding, so 8px more
// makes 32 from wherever the main content starts, sidebar open or closed.
export const ToastStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 8px;
  position: fixed;
  top: 19px;
  width: 990px;
  z-index: 300;
`;

// How long a copy toast stays up before clearing itself.
export const TOAST_TIMEOUT_MS = 5000;

// One per entry in the copy response (Figma 5059-64109): "{lesson} was copied in
// {class}" with a View link that jumps to that class's Groups view. Clears itself
// after TOAST_TIMEOUT_MS; the close button and the View link still dismiss early.
export const LessonCopiedBanner = ({ message, onView, onClose }) => {
  // Callers pass an inline arrow, so keying the timer on `onClose` would restart it
  // on every parent render and the toast would never clear. Start it once on mount
  // and dispatch through a ref that always holds the current handler.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const id = setTimeout(() => onCloseRef.current?.(), TOAST_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, []);

  return (
    <BannerFrame>
      <BannerText>
        <span>{message}</span>
        <BannerLink type="button" onClick={onView}>View</BannerLink>
      </BannerText>
      <BannerClose aria-label="Dismiss" onClick={onClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </BannerClose>
    </BannerFrame>
  );
};
